// ./src/pages/private/DashboardPage/DashboardPage.tsx
import React, { useCallback, useMemo, useEffect, useRef, useState } from "react";
import { Stack, Grid, Box } from "@mantine/core";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { PageProps } from "@sdflc/react-router-pages";
import { STATUSES } from "@sdflc/utils";

import { DEFAULT_PAGE_SIZE, EXPENSE_TYPES, OPERATION_RIGHTS, ENTITIES } from "@/config";

import { buildUrlWithFilters } from "@/utils/filterHelpers";
import { mapScanResultToExpense } from "@/utils/scanHelpers";
import { getCarDisplayName, getVehicleType } from "@/utils/carHelpers";

import { Car } from "@/utils/api/CarService";
import { Expense, ExpenseInput } from "@/utils/api/ExpenseService";
import { GloveboxDocument } from "@/utils/api/GloveboxDocumentService";
import { VehicleTask } from "@/utils/api/VehicleTaskService";
import { ReceiptScanResult } from "@/utils/api/ScannerService";
import { ParkingSession } from "@/utils/api/ParkingSessionService";

import { useExpenses } from "@/hooks/useExpenses";
import { useTravels } from "@/hooks/useTravels";
import { useServiceIntervalNexts } from "@/hooks/useServiceIntervalNexts";
import { useGloveboxDocuments } from "@/hooks/useGloveboxDocuments";
import { useEntityNavigation } from "@/hooks/useEntityNavigation";
import { useToast } from "@/hooks/useToast";
import { DetectLocationFn } from "@/hooks/useScanner";
import { useGoogleMapsAutocomplete } from "@/hooks/useGoogleMapsAutocomplete";

import { useApp } from "@/contexts/AppContext";
import { useDashboardContext } from "@/contexts/DashboardContext";
import { useEntityFiltersContext } from "@/contexts/EntityFiltersContext";
import { useActiveTravelContext } from "@/contexts/ActiveTravelContext";
import { useActiveParkingContext } from "@/contexts/ActiveParkingContext";
import { useLayoutUI } from "@/contexts/LayoutUIContext";
import { useAppEnvironment } from "@/contexts/AppEnvironmentContext";
import { VehicleTasksProvider, useVehicleTasksContext } from "@/contexts/VehicleTasksContext";

import { DashboardLayout } from "@/components/layouts/DashboardLayout";
import { PullToRefresh } from "@/components/generic/PullToRefresh";

import {
  DashboardSummaryBar,
  DashboardVehicleGrid,
  DashboardAlerts,
  DashboardRecentActivity,
  ActivityItem,
  DashboardUpcomingServices,
  DashboardExpiringDocuments,
  DashboardVehicleTasks,
  DashboardWelcome,
} from "@/components/features/Dashboard";

import {
  QuickActionType,
  MobileVehicleSwitcher,
  MobileVehicleCard,
} from "@/components/features/Vehicle";

import { StartTravelDrawer } from "@/components/features/Travels/StartTravelDrawer";
import { ActiveTravelActionDrawer } from "@/components/features/Travels/ActiveTravelActionDrawer";
import { VehicleTaskEditDrawer } from "@/components/features/VehicleTasks/VehicleTaskEditDrawer";
import { LogbookEditDrawer } from "@/components/features/Logbook/LogbookEditDrawer";
import { CarsEmptyState } from "@/components/features/Cars/CarsEmptyState";

// ============================================
// Types
// ============================================

/** Type for tracking which quick action drawer is open */
type QuickActionDrawerType =
  | "logbook"
  | "travel-start"
  | "travel-action"
  | "parking-start"
  | null;

/** State for the quick action drawer */
interface QuickActionDrawerState {
  type: QuickActionDrawerType;
  carId: string | null;
  /** Default record type for the logbook drawer */
  defaultRecordType?: number;
}

// ============================================
// Constants
// ============================================

/** Auto-refresh interval in milliseconds (1 minute) */
const STATS_REFRESH_INTERVAL_MS = 60 * 1000;

/** Urgency statuses to show on dashboard (overdue and due soon) */
const DASHBOARD_URGENCY_STATUSES: ("overdue" | "due_soon")[] = [
  "overdue",
  "due_soon",
];

/** Initial drawer state (closed) */
const INITIAL_DRAWER_STATE: QuickActionDrawerState = {
  type: null,
  carId: null,
  defaultRecordType: EXPENSE_TYPES.REFUEL,
};

/** Filter for expiring/expired glovebox documents */
const EXPIRING_DOCUMENTS_FILTER = {
  expiredOrExpiring: true,
  status: [STATUSES.ACTIVE],
};

/** Sort for expiring documents - expired first, then by expiration date */
const EXPIRING_DOCUMENTS_SORT = [
  { name: "expiresAt", order: "ASC" as const },
];

// ============================================
// Page Wrapper — provides VehicleTasksContext
// ============================================

/**
 * Main dashboard page.
 * Wrapped in VehicleTasksProvider so the VehicleTaskEditDrawer
 * can read get/create/update/isCreating/isUpdating/lastError/clearError
 * from context automatically.
 */
export const DashboardPage: React.FC<PageProps> = (props) => {
  return (
    <VehicleTasksProvider autoLoad={false}>
      <DashboardPageContent {...props} />
    </VehicleTasksProvider>
  );
};

// ============================================
// Page Content — consumes VehicleTasksContext
// ============================================

/**
 * Dashboard page content displaying:
 *
 * Desktop:
 * - Summary bar with monthly totals (when multiple vehicles)
 * - Vehicle cards grid with scan receipt per vehicle
 * - Vehicle tasks, expiring documents, upcoming services, recent activity
 * - Notifications/alerts sidebar
 *
 * Mobile:
 * - Vehicle switcher bar (tap to switch between vehicles)
 * - Single-vehicle dashboard filtered to the selected car
 * - Vehicle tasks, expiring documents, upcoming services, recent activity
 * - Quick action drawers for creating records without navigation
 */
const DashboardPageContent: React.FC<PageProps> = (props) => {
  const navigate = useNavigate();
  const { geocodeAddress } = useGoogleMapsAutocomplete();
  const { t } = useTranslation(["dashboard", "common", "travels", "vehicleTasks"]);
  const { user, hasRightTo } = useApp();
  const { setParkingSession } = useLayoutUI();
  const { isMobile } = useAppEnvironment();
  const toast = useToast();

  // ============================================
  // Contexts
  // ============================================

  const {
    cars,
    activeCar,
    activeCarId,
    activeItem,
    hasMultipleCars,
    hasCars,
    loading: carsLoading,
    getCarById,
    refresh: refreshDashboard,
    refreshStats,
  } = useDashboardContext();

  const { filters, /*setCarId,*/ persistVehicleId } = useEntityFiltersContext();

  const {
    activeTravel,
    activeTravelCarId,
    hasActiveTravel,
  } = useActiveTravelContext();

  const {
    isCarParked,
    activeSessionForCar,
  } = useActiveParkingContext();

  // Vehicle tasks from context
  const {
    dashboardTasks,
    isDashboardLoading: vehicleTasksLoading,
    dashboard: loadDashboardTasks,
    complete: completeTask,
    clearError: clearVehicleTaskError,
  } = useVehicleTasksContext();

  // Navigation for upcoming services report
  const { getListUrl: getUpcomingServicesUrl } = useEntityNavigation({
    entityType: "upcoming-services",
    prefix: "reports",
  });

  // Navigation for glovebox
  const { getListUrl: getGloveboxUrl } = useEntityNavigation({
    entityType: "glovebox",
  });

  // Navigation for vehicle tasks
  const { getListUrl: getVehicleTasksUrl } = useEntityNavigation({
    entityType: "vehicle-tasks",
  });

  // ============================================
  // Permissions
  // ============================================

  const canCreateExpense = hasRightTo(ENTITIES.EXPENSES, OPERATION_RIGHTS.CREATE);
  const canCreateTravel = hasRightTo(ENTITIES.TRAVELS, OPERATION_RIGHTS.CREATE);

  // ============================================
  // Derived State
  // ============================================

  /** True when cars have loaded and there are none — show empty/welcome state */
  const showEmptyState = !carsLoading && !hasCars;

  // ============================================
  // Quick Action Drawer State
  // ============================================

  const [quickActionDrawer, setQuickActionDrawer] =
    React.useState<QuickActionDrawerState>(INITIAL_DRAWER_STATE);

  // Task edit drawer state — simplified: just track open state and record ID
  const [isTaskDrawerOpen, setIsTaskDrawerOpen] = useState(false);
  const [taskDrawerRecordId, setTaskDrawerRecordId] = useState<string | null>(null);

  // Track which task is being completed (for per-item loading)
  const [completingTaskId, setCompletingTaskId] = useState<string | null>(null);

  // Pre-fill data from receipt scan (Partial<Expense> used as initialData)
  const [scannedExpense, setScannedExpense] = useState<Partial<Expense> | null>(null);

  // Logbook edit record ID
  const [logbookRecordId, setLogbookRecordId] = useState<string | null>(null);

  // Track if component is mounted for cleanup
  const isMountedRef = useRef(true);

  // ============================================
  // Mobile Vehicle Selection
  // ============================================

  /**
   * Handle vehicle selection from the MobileVehicleSwitcher.
   * Persists the choice to device storage and updates the filter context.
   */
  const handleMobileVehicleSelect = useCallback(
    (carId: string) => {
      persistVehicleId(carId);
    },
    [persistVehicleId]
  );

  // ============================================
  // Data Hooks
  // ============================================

  // Expenses hook for creating refuels, expenses, checkpoints, and waypoints
  const {
    create: createExpense,
    update: updateExpense,
    get: loadExpense,
    isCreating: isCreatingExpense,
    isUpdating: isUpdatingExpense,
    lastError: expenseError,
    clearError: clearExpenseError,
  } = useExpenses({
    autoLoad: false,
  });

  // Recent expenses for activity feed
  const {
    expenses,
    loading: expensesLoading,
    refresh: refreshExpenses,
  } = useExpenses({
    autoLoad: true,
    initialFilters: {},
    pageSize: 10,
    initialSort: [{ name: "whenDone", order: "DESC" }],
  });

  // Recent travels for activity feed
  const {
    travels,
    loading: travelsLoading,
    refresh: refreshTravels,
  } = useTravels({
    autoLoad: true,
    initialFilters: {},
    pageSize: DEFAULT_PAGE_SIZE,
    initialSort: [{ name: "firstDttm", order: "DESC" }],
  });

  // Upcoming services hook - filter to overdue and due_soon only
  const {
    serviceIntervalNexts: upcomingServices,
    loading: upcomingServicesLoading,
    refresh: refreshUpcomingServices,
  } = useServiceIntervalNexts({
    autoLoad: true,
    userId: user?.id,
    urgencyStatus: DASHBOARD_URGENCY_STATUSES,
  });

  // Expiring glovebox documents hook
  const {
    documents: expiringDocuments,
    loading: expiringDocumentsLoading,
    list: listExpiringDocuments,
  } = useGloveboxDocuments({
    autoLoad: false, // We'll load manually with specific filter
  });

  // ============================================
  // Initial Data Loading
  // ============================================

  // Load expiring documents on mount
  useEffect(() => {
    if (user?.id) {
      listExpiringDocuments(
        EXPIRING_DOCUMENTS_FILTER,
        { page: 1, pageSize: 100 }, // Load all expiring documents
        EXPIRING_DOCUMENTS_SORT,
        true
      );
    }
  }, [user?.id, listExpiringDocuments]);

  // Load dashboard tasks on mount
  useEffect(() => {
    if (user?.id) {
      loadDashboardTasks();
    }
  }, [user?.id, loadDashboardTasks]);

  // Refresh expiring documents helper
  const refreshExpiringDocuments = useCallback(async () => {
    await listExpiringDocuments(
      EXPIRING_DOCUMENTS_FILTER,
      { page: 1, pageSize: 100 },
      EXPIRING_DOCUMENTS_SORT,
      true
    );
  }, [listExpiringDocuments]);

  // Refresh dashboard tasks helper
  const refreshDashboardTasks = useCallback(async () => {
    await loadDashboardTasks();
  }, [loadDashboardTasks]);

  /** Handle receipt scan completion — map result and open appropriate drawer */
  const handleDetectLocation: DetectLocationFn = useCallback(
    async (result) => {
      const addressToGeocode = result.fields?.location || result.fields?.whereDone;

      if (!addressToGeocode) {
        return result;
      }

      const addressData = await geocodeAddress(addressToGeocode);

      if (!addressData?.latitude || !addressData?.longitude) {
        return result;
      }

      // Return enriched result — useScanner stores this as the final scanResult,
      // which then gets passed to handleScanComplete via onScanComplete.
      return {
        ...result,
        _geocoded: {
          latitude: addressData.latitude,
          longitude: addressData.longitude,
          city: addressData.city || undefined,
          region: addressData.region || undefined,
          postalCode: addressData.postalCode || undefined,
          country: addressData.country || undefined,
        },
      };
    },
    [geocodeAddress]
  );

  /**
   * Handle scan completion from a vehicle card.
   * Maps the scan result to an expense and opens the logbook drawer
   * with the carId pre-selected and appropriate default record type.
   */
  const handleScanComplete = useCallback(
    (result: ReceiptScanResult, carId?: string | null | undefined) => {
      const expense = mapScanResultToExpense(result);

      expense.carId = carId ?? undefined;

      // Apply geocoded data if available
      const geo = result._geocoded;

      if (geo) {
        if (geo.latitude) expense.latitude = geo.latitude;
        if (geo.longitude) expense.longitude = geo.longitude;
        if (geo.city && !expense.city) expense.city = geo.city;
        if (geo.region && !expense.stateProvince) expense.stateProvince = geo.region;
        if (geo.postalCode && !expense.postalCode) expense.postalCode = geo.postalCode;
        if (geo.country && !expense.countryId) expense.countryId = geo.country;
      }

      setScannedExpense(expense);

      const defaultType = result.expenseType === 1
        ? EXPENSE_TYPES.REFUEL
        : EXPENSE_TYPES.EXPENSE;

      setQuickActionDrawer({ type: "logbook", carId: carId || null, defaultRecordType: defaultType });
    },
    []
  );

  // ============================================
  // Memoized Values
  // ============================================

  // Create cars map for activity labels
  const carsMap = useMemo(() => {
    const map = new Map<string, Car>();
    cars.forEach((car) => {
      if (car.id) {
        map.set(car.id, car);
      }
    });
    return map;
  }, [cars]);

  // ============================================
  // Auto-refresh stats on page visit and interval
  // ============================================

  useEffect(() => {
    isMountedRef.current = true;

    // Refresh stats immediately when user comes to the page
    refreshStats();

    // Set up interval to refresh stats every minute while on page
    const intervalId = setInterval(() => {
      if (isMountedRef.current) {
        refreshStats();
      }
    }, STATS_REFRESH_INTERVAL_MS);

    // Cleanup on unmount
    return () => {
      isMountedRef.current = false;
      clearInterval(intervalId);
    };
  }, [refreshStats]);

  // ============================================
  // Quick Action Drawer Handlers
  // ============================================

  /** Close the quick action drawer and clear any errors */
  const handleCloseQuickActionDrawer = useCallback(() => {
    setQuickActionDrawer(INITIAL_DRAWER_STATE);
    setScannedExpense(null);
    setLogbookRecordId(null);
    clearExpenseError();
  }, [clearExpenseError]);

  /** Refresh dashboard data after successful creation */
  const refreshDashboardData = useCallback(async () => {
    await Promise.all([
      refreshStats(),
      refreshExpenses(),
    ]);
  }, [refreshStats, refreshExpenses]);

  /** Handle logbook record creation (refuel, expense, or checkpoint).
   *  Returns OpResult so LogbookEditDrawer can check didSucceed() —
   *  the drawer stays open on failure to display validation errors. */
  const handleCreateLogbookRecord = useCallback(
    async (data: ExpenseInput) => {
      const result = await createExpense(data);

      if (result?.didSucceed()) {
        // Show type-specific success toast
        const typeKey =
          data.expenseType === EXPENSE_TYPES.REFUEL
            ? "refuelCreated"
            : data.expenseType === EXPENSE_TYPES.CHECKPOINT
              ? "checkpointCreated"
              : "expenseCreated";
        toast.success(t(`common:dashboard.quickActions.${typeKey}`));

        await refreshDashboardData();
      }

      // Return result to the drawer — it will close itself on success
      // and stay open showing errors on failure
      return result;
    },
    [createExpense, toast, t, refreshDashboardData]
  );

  /** Handle starting a new travel */
  const handleStartTravel = useCallback(
    async () => {
      handleCloseQuickActionDrawer();
      toast.success(t("common:dashboard.quickActions.travelStarted"));
      refreshDashboardData();
      refreshTravels();
    },
    [handleCloseQuickActionDrawer, toast, t, refreshDashboardData, refreshTravels]
  );

  /** Handle adding a waypoint via the unified action drawer */
  const handleWaypointAdded = useCallback(
    async () => {
      handleCloseQuickActionDrawer();
      toast.success(t("common:dashboard.quickActions.waypointAdded"));
      refreshDashboardData();
    },
    [handleCloseQuickActionDrawer, toast, t, refreshDashboardData]
  );

  /** Handle completing a trip via the unified action drawer */
  const handleTravelCompleted = useCallback(
    async () => {
      handleCloseQuickActionDrawer();
      toast.success(t("common:dashboard.quickActions.travelCompleted"));
      refreshDashboardData();
      refreshTravels();
    },
    [handleCloseQuickActionDrawer, toast, t, refreshDashboardData, refreshTravels]
  );

  // ============================================
  // Vehicle Task Handlers
  // ============================================

  /** Handle completing a task from the dashboard */
  const handleCompleteTask = useCallback(
    async (task: VehicleTask) => {
      if (!task.id) return;

      setCompletingTaskId(task.id);

      const result = await completeTask(task.id);

      if (result.code === 0) {
        toast.success(t("vehicleTasks:dashboard.taskCompleted"));
        // Refresh dashboard tasks to update the list
        await refreshDashboardTasks();
      }

      setCompletingTaskId(null);
    },
    [completeTask, toast, t, refreshDashboardTasks]
  );

  /** Handle opening the edit drawer for a task */
  const handleEditTask = useCallback((task: VehicleTask) => {
    setTaskDrawerRecordId(task.id || null);
    setIsTaskDrawerOpen(true);
  }, []);

  /** Handle opening the drawer in create mode */
  const handleOpenCreateTaskDrawer = useCallback(() => {
    setTaskDrawerRecordId(null);
    setIsTaskDrawerOpen(true);
  }, []);

  /** Handle closing the task edit drawer */
  const handleCloseTaskDrawer = useCallback(() => {
    setIsTaskDrawerOpen(false);
    setTaskDrawerRecordId(null);
    clearVehicleTaskError();
  }, [clearVehicleTaskError]);

  /** Called by the drawer after a task is successfully created */
  const handleTaskCreated = useCallback(
    async (_task: VehicleTask) => {
      toast.success(t("vehicleTasks:notifications.createSuccess"));
      handleCloseTaskDrawer();
      await refreshDashboardTasks();
    },
    [toast, t, handleCloseTaskDrawer, refreshDashboardTasks]
  );

  /** Called by the drawer after a task is successfully updated */
  const handleTaskUpdated = useCallback(
    async (_task: VehicleTask) => {
      toast.success(t("vehicleTasks:dashboard.taskUpdated"));
      handleCloseTaskDrawer();
      await refreshDashboardTasks();
    },
    [toast, t, handleCloseTaskDrawer, refreshDashboardTasks]
  );

  /** Handle navigating to the full tasks list */
  const handleViewAllTasks = useCallback(() => {
    navigate(getVehicleTasksUrl());
  }, [navigate, getVehicleTasksUrl]);

  // ============================================
  // Vehicle Card Handlers
  // ============================================

  /** Handle quick action button click on vehicle card */
  const handleQuickAction = useCallback(
    (action: QuickActionType, carId: string) => {
      // Handle travel action separately due to active travel logic
      if (action === "travel") {
        // Check permission for travel
        if (!canCreateTravel) {
          toast.error(t("common:errors.noPermission"));
          return;
        }

        // Check if there's an active travel
        if (hasActiveTravel) {
          if (activeTravelCarId === carId) {
            // Same car — open unified active travel action drawer
            setQuickActionDrawer({ type: "travel-action", carId });
          } else {
            // Different car - show error
            const activeTravelCar = activeTravelCarId
              ? getCarById(activeTravelCarId)
              : null;
            const carName = activeTravelCar
              ? getCarDisplayName(activeTravelCar)
              : t("common:vehicle");
            toast.error(
              t("common:dashboard.quickActions.activeTravelExists", { carName })
            );
          }
        } else {
          // No active travel - open start travel drawer
          setQuickActionDrawer({ type: "travel-start", carId, defaultRecordType: EXPENSE_TYPES.REFUEL });
        }
        return;
      }

      if (action === "parking") {
        if (isCarParked(carId)) {
          // Car already parked — open detail drawer via layout context
          const session = activeSessionForCar(carId);
          if (session) {
            setParkingSession(session);
          }
          return;
        }

        // Not parked — open start parking drawer via layout context
        setParkingSession({ carId } as ParkingSession);
        return;
      }

      // For non-travel actions, check expense permission
      if (!canCreateExpense) {
        toast.error(t("common:errors.noPermission"));
        return;
      }

      // Map action type to default record type for the logbook drawer
      const recordTypeMap: Record<string, number> = {
        refuel: EXPENSE_TYPES.REFUEL,
        expense: EXPENSE_TYPES.EXPENSE,
        checkpoint: EXPENSE_TYPES.CHECKPOINT,
      };

      const defaultRecordType = recordTypeMap[action] || EXPENSE_TYPES.REFUEL;
      setQuickActionDrawer({ type: "logbook", carId, defaultRecordType });
    },
    [
      canCreateExpense,
      canCreateTravel,
      hasActiveTravel,
      activeTravelCarId,
      isCarParked,
      getCarById,
      toast,
      t,
    ]
  );

  // Handle pull-to-refresh (returns Promise for the component)
  const handlePullToRefresh = useCallback(async (): Promise<void> => {
    await Promise.all([
      refreshDashboard(),
      refreshExpenses(),
      refreshTravels(),
      refreshUpcomingServices(),
      refreshExpiringDocuments(),
      refreshDashboardTasks(),
    ]);
  }, [refreshDashboard, refreshExpenses, refreshTravels, refreshUpcomingServices, refreshExpiringDocuments, refreshDashboardTasks]);

  const handleVehicleClick = useCallback(
    (car: Car) => {
      if (car.id) {
        navigate(`/cars/${car.id}`);
      }
    },
    [navigate]
  );

  const handleAddVehicle = useCallback(() => {
    navigate("/cars/add");
  }, [navigate]);

  const handleActivityClick = useCallback(
    (item: ActivityItem) => {
      // Trips (waypoints) are not editable via logbook drawer
      if (item.type === "trip") return;

      // Travels open navigation (keep existing behavior)
      if (item.type === "travel") {
        const url = buildUrlWithFilters(`/travels/${item.id}/edit`, filters);
        navigate(url);
        return;
      }

      // Expenses, refuels, checkpoints, revenues → open drawer in edit mode
      setLogbookRecordId(item.id);
      setQuickActionDrawer({
        type: "logbook",
        carId: item.carId || null,
      });
    },
    [navigate, filters]
  );

  // Add onUpdated handler
  const handleLogbookRecordUpdated = useCallback(
    async (_record: Expense) => {
      toast.success(t("common:dashboard.quickActions.recordUpdated"));
      handleCloseQuickActionDrawer();
      await refreshDashboardData();
    },
    [toast, t, handleCloseQuickActionDrawer, refreshDashboardData]
  );

  const handleViewAllActivity = useCallback(() => {
    // Navigate to activity page with filters preserved
    const url = buildUrlWithFilters("/logbook", filters);
    navigate(url);
  }, [navigate, filters]);

  // Handler for adding a record for a service
  const handleAddServiceRecord = useCallback(
    (carId: string, kindId: number) => {
      // Build initialData with kindId pre-filled
      setScannedExpense({
        carId,
        kindId,
        expenseType: EXPENSE_TYPES.EXPENSE,
      });
      setQuickActionDrawer({
        type: "logbook",
        carId,
        defaultRecordType: EXPENSE_TYPES.EXPENSE,
      });
    },
    []
  );

  // Handler for viewing upcoming services report
  const handleViewUpcomingServicesReport = useCallback(() => {
    navigate(getUpcomingServicesUrl());
  }, [navigate, getUpcomingServicesUrl]);

  // Handler for updating a glovebox document
  const handleUpdateDocument = useCallback(
    (document: GloveboxDocument) => {
      if (document.id) {
        const url = buildUrlWithFilters(`/glovebox/${document.id}/edit`, filters);
        navigate(url);
      }
    },
    [navigate, filters]
  );

  // Handler for viewing all glovebox documents (filtered to expiring)
  const handleViewAllGlovebox = useCallback(() => {
    // Navigate to glovebox with expiredOrExpiring filter
    const gloveboxUrl = getGloveboxUrl();
    const url = `${gloveboxUrl}${gloveboxUrl.includes("?") ? "&" : "?"}expiredOrExpiring=true`;
    navigate(url);
  }, [navigate, getGloveboxUrl]);

  // ============================================
  // Render
  // ============================================

  return (
    <DashboardLayout {...props}>
      <Box style={{ height: "100%", position: "relative" }}>
        <PullToRefresh onRefresh={handlePullToRefresh}>
          <Stack gap="lg">

            {/* ============================================ */}
            {/* Empty State: No vehicles — welcome + CTA */}
            {/* ============================================ */}
            {showEmptyState && (
              <>
                <DashboardWelcome userName={user?.firstName} />
                <CarsEmptyState
                  display
                  onCreateCar={handleAddVehicle}
                />
              </>
            )}

            {/* ============================================ */}
            {/* Mobile: Vehicle Switcher + Stats Card */}
            {/* ============================================ */}
            {!showEmptyState && isMobile && hasCars && (
              <>
                <MobileVehicleSwitcher
                  selectedCar={activeCar}
                  cars={cars}
                  hasMultipleCars={hasMultipleCars}
                  onSelect={handleMobileVehicleSelect}
                />

                {activeItem && activeCarId && (
                  <MobileVehicleCard
                    item={activeItem}
                    carId={activeCarId}
                    vehicleType={getVehicleType(activeCar)}
                    onQuickAction={handleQuickAction}
                    showScanReceipt={canCreateExpense}
                    onScanComplete={handleScanComplete}
                    onDetectLocation={handleDetectLocation}
                  />
                )}
              </>
            )}

            {/* Summary Bar — desktop only, only when multiple vehicles */}
            {!showEmptyState && !isMobile && hasMultipleCars && <DashboardSummaryBar />}

            {/* Main Content Grid */}
            {!showEmptyState && (
              <Grid gutter="lg">
                {/* Left Column: Vehicles + Tasks + Expiring Documents + Upcoming Services + Activity */}
                <Grid.Col span={{ base: 12, lg: 8 }}>
                  <Stack gap="lg">
                    {/* Vehicle Grid — desktop shows all, mobile is handled by switcher above */}
                    {!isMobile && (
                      <DashboardVehicleGrid
                        onQuickAction={handleQuickAction}
                        onVehicleClick={handleVehicleClick}
                        onAddVehicle={handleAddVehicle}
                        showScanReceipt={canCreateExpense}
                        onScanComplete={handleScanComplete}
                        onDetectLocation={handleDetectLocation}
                      />
                    )}

                    {/* Vehicle Tasks */}
                    <DashboardVehicleTasks
                      tasks={dashboardTasks}
                      loading={vehicleTasksLoading}
                      onComplete={handleCompleteTask}
                      onEdit={handleEditTask}
                      onCreate={handleOpenCreateTaskDrawer}
                      onViewAll={handleViewAllTasks}
                      isCompleting={!!completingTaskId}
                      completingTaskId={completingTaskId}
                    />

                    {/* Expiring Glovebox Documents */}
                    {!expiringDocumentsLoading && expiringDocuments.length > 0 && (
                      <DashboardExpiringDocuments
                        documents={expiringDocuments}
                        loading={expiringDocumentsLoading}
                        onUpdateDocument={handleUpdateDocument}
                        onViewAll={handleViewAllGlovebox}
                      />
                    )}

                    {/* Upcoming Services */}
                    {!upcomingServicesLoading && upcomingServices.length > 0 && (
                      <DashboardUpcomingServices
                        services={upcomingServices}
                        loading={upcomingServicesLoading}
                        onAddRecord={handleAddServiceRecord}
                        onViewReport={handleViewUpcomingServicesReport}
                      />
                    )}

                    {/* Recent Activity */}
                    <DashboardRecentActivity
                      expenses={expenses}
                      travels={travels}
                      carsMap={carsMap}
                      loading={expensesLoading || travelsLoading}
                      maxItems={10}
                      onActivityClick={handleActivityClick}
                      onViewAll={handleViewAllActivity}
                    />
                  </Stack>
                </Grid.Col>

                {/* Right Column: Alerts — desktop only */}
                {!isMobile && (
                  <Grid.Col span={{ base: 12, lg: 4 }}>
                    <DashboardAlerts maxItems={5} />
                  </Grid.Col>
                )}
              </Grid>
            )}

            {/* Alerts — mobile: shown inline at the bottom */}
            {!showEmptyState && isMobile && (
              <DashboardAlerts maxItems={5} />
            )}
          </Stack>
        </PullToRefresh>
      </Box>

      {/* ============================================ */}
      {/* Quick Action Drawers */}
      {/* ============================================ */}

      {/* Logbook Drawer (Refuel / Expense / Checkpoint) — create-only from dashboard */}
      <LogbookEditDrawer
        opened={quickActionDrawer.type === "logbook"}
        onClose={handleCloseQuickActionDrawer}
        recordId={logbookRecordId}
        initialData={scannedExpense}
        lastRecord={null}
        onLoad={loadExpense}
        onCreate={handleCreateLogbookRecord}
        onUpdate={updateExpense}
        onUpdated={handleLogbookRecordUpdated}
        isCreating={isCreatingExpense}
        isUpdating={isUpdatingExpense}
        lastError={expenseError}
        onClearError={clearExpenseError}
        initialCarId={quickActionDrawer.carId}
        defaultRecordType={quickActionDrawer.defaultRecordType}
      />

      {/* Start Travel Drawer */}
      <StartTravelDrawer
        opened={quickActionDrawer.type === "travel-start"}
        onClose={handleCloseQuickActionDrawer}
        onTravelStarted={handleStartTravel}
        defaultCarId={quickActionDrawer.carId}
        previousTravel={null}
      />

      {/* Unified Active Travel Action Drawer (Add Waypoint / Complete Trip)
          Replaces the old AddWaypointDrawer for the quick action flow */}
      <ActiveTravelActionDrawer
        opened={quickActionDrawer.type === "travel-action"}
        onClose={handleCloseQuickActionDrawer}
        activeTravel={activeTravel}
        onWaypointAdded={handleWaypointAdded}
        onTravelCompleted={handleTravelCompleted}
      />

      {/* Vehicle Task Edit Drawer
          - Reads get/create/update/isCreating/isUpdating/lastError/clearError
            from VehicleTasksContext automatically
          - onCreated/onUpdated handle toast + close + refresh */}
      <VehicleTaskEditDrawer
        opened={isTaskDrawerOpen}
        onClose={handleCloseTaskDrawer}
        recordId={taskDrawerRecordId}
        onCreated={handleTaskCreated}
        onUpdated={handleTaskUpdated}
        initialCarId={filters?.carId || (cars.length === 1 ? cars[0]?.id : undefined) || undefined}
      />
    </DashboardLayout>
  );
};